
import type { RoleAssignmentDTO } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { RoleAssignmentMessages } from '../../models'
import { computed, defineComponent, ref, watch } from 'vue'
import { useConditionValues } from '../../composables/users/use-condition-values'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { EpbText } from '../shared/epb-text'
import RoleAssignmentCondition from './role-assignment-condition.component.vue'

export default defineComponent({
  name: 'RoleAssignmentConditions',
  components: {
    EpbText,
    RoleAssignmentCondition,
  },
  props: {
    value: {
      type: Array as PropType<RoleAssignmentDTO['conditions']>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<RoleAssignmentMessages['conditions']>,
    },
  },
  emits: {
    input: (value: RoleAssignmentDTO['conditions']) => {},
  },
  setup(props, { emit }) {
    const { getConditionValues } = useConditionValues()

    const baseClass = 'epb-role-assignment-conditions'

    const selectedCondition = ref<RoleAssignmentDTO['conditions'][0]['condition'] | undefined>(
      undefined,
    )

    const visibleValues = computed(() =>
      props.value.filter(valueItem => getConditionValues(valueItem.condition)?.length !== 1),
    )

    const selectedValue = computed<RoleAssignmentDTO['conditions'][0] | undefined>(() =>
      visibleValues.value.find(visibleValue => visibleValue.condition === selectedCondition.value),
    )

    function updateValueWith(valueItem: RoleAssignmentDTO['conditions'][0]): void {
      const newValue = [...props.value]
      const index = newValue.findIndex(
        newValueItem => newValueItem.condition === valueItem.condition,
      )
      newValue.splice(index, 1, valueItem)
      emit('input', newValue)
    }

    watch(
      () => props.value,
      value => {
        if (!props.readonly) {
          value.forEach(valueItem => {
            if (!valueItem.value) {
              const valueItemConditionValues = getConditionValues(valueItem.condition)
              if (valueItemConditionValues?.length === 1) {
                updateValueWith({
                  ...valueItem,
                  value: valueItemConditionValues[0],
                })
              }
            }
          })
        }
        if (
          !selectedCondition.value ||
          !visibleValues.value.some(
            visibleValue => visibleValue.condition === selectedCondition.value,
          )
        ) {
          selectedCondition.value = value[0]?.condition
        }
      },
      { immediate: true },
    )

    return {
      baseClass,
      selectedCondition,
      visibleValues,
      selectedValue,
      updateValueWith,
      capitalizeSentence,
    }
  },
})
